import React, { useSyncExternalStore } from 'react';

function subscribeReducedMotion(callback) {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

function getReducedMotionSnapshot() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getReducedMotionServerSnapshot() {
  return false;
}

/** Pointy-top hex path (R = center → vertex, circumradius). */
export function hexPathPointyTop(cx, cy, R) {
  const pts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    pts.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
  }
  return `M ${pts.map((p) => p.join(' ')).join(' L ')} Z`;
}

const VIEW = 200;
const CX = VIEW / 2;
const CY = VIEW / 2;
/** Single size for core + ring — true honeycomb (all cells share edges with neighbors). */
const R = 32;
const SQRT3 = Math.sqrt(3);

/** Six neighbors of a pointy-top hex at (CX, CY); same circumradius R. */
const NEIGHBOR_OFFSETS = [
  [SQRT3 * R, 0],
  [SQRT3 * R / 2, -1.5 * R],
  [-SQRT3 * R / 2, -1.5 * R],
  [-SQRT3 * R, 0],
  [-SQRT3 * R / 2, 1.5 * R],
  [SQRT3 * R / 2, 1.5 * R],
];

const NEIGHBOR_HEXES = NEIGHBOR_OFFSETS.map(([dx, dy], i) => {
  const styles = [
    {
      key: 'e',
      stroke: 'rgba(251, 146, 60, 0.88)',
      sw: 1.15,
      strokePulse: '0.38;0.95;0.38',
      dur: '2.75s',
      begin: '0.2s',
    },
    {
      key: 'ne',
      stroke: 'rgba(249, 115, 22, 0.85)',
      sw: 1.1,
      strokePulse: '0.38;0.95;0.38',
      dur: '2.75s',
      begin: '0.45s',
    },
    {
      key: 'nw',
      stroke: 'rgba(253, 186, 116, 0.72)',
      sw: 1,
      strokePulse: '0.38;0.95;0.38',
      dur: '2.75s',
      begin: '0.7s',
    },
    {
      key: 'w',
      stroke: 'rgba(251, 146, 60, 0.82)',
      sw: 1.1,
      strokePulse: '0.38;0.95;0.38',
      dur: '2.75s',
      begin: '0.95s',
    },
    {
      key: 'sw',
      stroke: 'rgba(239, 68, 68, 0.78)',
      sw: 1.05,
      strokePulse: '0.38;0.95;0.38',
      dur: '2.75s',
      begin: '1.45s',
    },
    {
      key: 'se',
      stroke: 'rgba(220, 38, 38, 0.72)',
      sw: 1.05,
      strokePulse: '0.38;0.95;0.38',
      dur: '2.75s',
      begin: '1.2s',
    },
  ];
  const s = styles[i];
  return {
    cx: CX + dx,
    cy: CY + dy,
    R,
    fill: 'none',
    stroke: s.stroke,
    sw: s.sw,
    strokePulse: s.strokePulse,
    dur: s.dur,
    begin: s.begin,
    key: s.key,
  };
});

const CORE_HEX = {
  cx: CX,
  cy: CY,
  R,
  key: 'core',
  fill: 'rgba(255, 252, 245, 0.16)',
  stroke: 'rgba(254, 243, 199, 0.92)',
  sw: 1.45,
  strokePulse: '0.82;1;0.82',
  fillPulse: '0.75;1;0.75',
  dur: '3.4s',
  begin: '0s',
};

/**
 * Infra mark: compact honeycomb — one core hex + six neighbors (no outer “wings”).
 * Square viewBox so the cluster fits a single box; staggered stroke pulse on hexes only.
 */
export function InfraHexClusterSvg({ uid, ...svgAttrs }) {
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );

  const gid = `infra-panel-${uid}`;
  const hexes = [CORE_HEX, ...NEIGHBOR_HEXES];

  return (
    <svg viewBox={`0 0 ${VIEW} ${VIEW}`} xmlns="http://www.w3.org/2000/svg" {...svgAttrs}>
      <defs>
        <radialGradient id={gid} cx="50%" cy="48%" r="62%">
          <stop offset="0%" stopColor="rgba(255, 250, 240, 0.14)" />
          <stop offset="45%" stopColor="rgba(251, 146, 60, 0.06)" />
          <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
        </radialGradient>
      </defs>
      <rect width={VIEW} height={VIEW} rx="16" ry="16" fill={`url(#${gid})`} opacity="0.35" />
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {hexes.map((h) => (
          <path key={h.key} d={hexPathPointyTop(h.cx, h.cy, h.R)} fill={h.fill} stroke={h.stroke} strokeWidth={h.sw}>
            {!reducedMotion ? (
              <animate
                attributeName="stroke-opacity"
                values={h.strokePulse}
                keyTimes="0;0.5;1"
                calcMode="spline"
                keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
                dur={h.dur}
                begin={h.begin}
                repeatCount="indefinite"
              />
            ) : null}
            {!reducedMotion && h.fillPulse ? (
              <animate
                attributeName="fill-opacity"
                values={h.fillPulse}
                keyTimes="0;0.5;1"
                calcMode="spline"
                keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
                dur={h.dur}
                begin={h.begin}
                repeatCount="indefinite"
              />
            ) : null}
          </path>
        ))}
      </g>
    </svg>
  );
}
